import { post } from "aws-amplify/api";
import { default as React, useState, useMemo } from "react";
import { Button, Col, Row, Nav } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PacmanLoader } from 'react-spinners';
import EditableCell from "containers/common/EditableCell";
import { GenericTable } from "components/GenericTable";
import "containers/common/List.css";
import "containers/common/Table.css";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";

import {
    createColumnHelper,
} from "@tanstack/react-table";

function OwnerPageList({
    data,
    isLoading,
    isAuthenticated,
    controlType,
    showAddButton = false,
    properties,
    saleid,
    saveRecord,
    buyerSellers,
    existingOwnerParents,
    updateTableState,
    refreshDataSet,
    onOwnerParentAdded,
    ownerid,
    user,
    onClose,
}) {

    const [sorting, setSorting] = useState([]);

    // --- TanStack Table Setup ---
    const tableData = data || [];
    const columnHelper = createColumnHelper();

    function getBrowseOwnerIds(clickedOwnerId) {
        const selectedOwnerIds = tableData
            .filter((row) => row && row.selected)
            .map((row) => row.owner_id)
            .filter((ownerId) => ownerId !== null && ownerId !== undefined && ownerId !== "");

        const clickedId = clickedOwnerId !== null && clickedOwnerId !== undefined && clickedOwnerId !== ""
            ? clickedOwnerId
            : null;

        if (clickedId === null) {
            return selectedOwnerIds;
        }

        const clickedIdString = String(clickedId);
        const hasClickedInSelected = selectedOwnerIds.some((ownerId) => String(ownerId) === clickedIdString);

        if (selectedOwnerIds.length === 0) {
            return [clickedId];
        }

        if (hasClickedInSelected) {
            return selectedOwnerIds;
        }

        return [clickedId, ...selectedOwnerIds];
    }

    function isBrowseMultiSelect() {
        return tableData.filter((row) => row && row.selected).length > 1;
    }

    const col_owner_id = columnHelper.accessor("owner_id", {
        id: "owner_id",
        header: "Owner ID",
        cell: (e) => (
            <Link
                to={`../owners/${e.getValue()}`}
                state={{
                    data: getBrowseOwnerIds(e.getValue()),
                    isBrowseMultiSelect: isBrowseMultiSelect(),
                }}
            >
                {e.getValue()}
            </Link>
        ),
        size: 60,
    });
    const col_name = columnHelper.accessor("owner_name", {
        id: "owner_name",
        header: "Owner Name",
        cell: (info) => info.getValue(),
    });
    const col_owner_index_name = columnHelper.accessor("owner_index_name", {
        id: "owner_index_name",
        header: "Index Name",
        cell: (info) => info.getValue(),
    });
    const col_property_count = columnHelper.accessor("property_count", {
        id: "property_count",
        header: "Owned Properties",
        cell: (info) => info.getValue(),
        size: 120,
    });
    const col_address_line_1 = columnHelper.accessor("address_line_1", {
        id: "address_line_1",
        header: "Address Line 1",
        cell: (info) => info.getValue(),
        size: 200,
    });
    const col_address_line_2 = columnHelper.accessor("address_line_2", {
        id: "address_line_2",
        header: "Address Line 2",
        cell: (info) => info.getValue(),
        size: 200,
    });
    const col_suburb = columnHelper.accessor("suburb", {
        id: "suburb",
        header: "Suburb",
        cell: (info) => info.getValue(),
        size: 150
    });
    const col_postcode = columnHelper.accessor("postcode", {
        id: "postcode",
        header: "Postcode",
        cell: (info) => info.getValue(),
        size: 60,
    })
    const col_state = columnHelper.accessor("state", {
        id: "state",
        header: "State",
        cell: (info) => info.getValue(),
        size: 60,
    })
    const col_selected = columnHelper.accessor("selected", {
        id: "selected",
        header: "",
        cell: EditableCell,
        enableSorting: false,
        size: 50,
        meta: {
            className: "text-center p-0",
        }
    })
    const col_add_owner_to_prop_sale_id = columnHelper.accessor("add_owner_to_prop_sale_id", {
        id: "add_owner_to_prop_sale_id",
        header: "",
        size: 180,
        cell: row => (
            <Button type="button" size="sm" variant="secondary" onClick={(e) => handleAddOwnerToPropertyAndSale(row, e)} style={{backgroundColor:"#0d6efd"}}>
                Add to Sale
            </Button>
        )
    })
    const col_add_owner_to_prop_id = columnHelper.accessor("add_owner_to_prop_id", {
        id: "add_owner_to_prop_id",
        header: "",
        size: 140,
        cell: row => <Button type="button" size="sm" variant="primary" onClick={(e) => handleAddOwnerToProperty(row, e)}>Add to Property</Button>
    })
    const col_add_owner_to_owner_id = columnHelper.display({
        id: "add_owner_to_owner_id",
        header: "",
        cell: ({ row }) => <Button type="button" size="sm" variant="primary" onClick={(e) => handleAddOwnerToOwner(row.original.owner_id, e)}>Add as Parent</Button>,
        size: 120,
    })

    const columns = [
        col_selected,
        col_owner_id,
        col_name,
        col_owner_index_name,
        col_property_count,
        col_address_line_1,
        col_address_line_2,
        col_suburb,
        col_postcode,
        col_state,
    ];

    const columns1 = [
        col_owner_id,
        col_name,
        col_owner_index_name,
        col_property_count,
        col_address_line_1,
        col_address_line_2,
        col_suburb,
        col_postcode,
        col_state,
        col_add_owner_to_prop_sale_id,
    ];

    const columns2 = [
        col_owner_id,
        col_name,
        col_owner_index_name,
        col_property_count,
        col_address_line_1,
        col_address_line_2,
        col_suburb,
        col_postcode,
        col_state,
        col_add_owner_to_prop_id,
    ];

    const columns3 = [
        col_owner_id,
        col_name,
        col_owner_index_name,
        col_property_count,
        col_address_line_1,
        col_address_line_2,
        col_suburb,
        col_postcode,
        col_state,
        col_add_owner_to_owner_id,
    ];



    const createId = useMemo(() => createElementIdFactory("owner-page-list"), []);
    const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

    function getOwnerRecord(ownerContext) {
        if (!ownerContext) {
            return null;
        }

        // Support legacy react-table row shape and TanStack cell context shape.
        return ownerContext.original || ownerContext.row?.original || ownerContext.row || ownerContext;
    }

    // --- Data Fetching and Update Functions ---
    async function handleAddOwnerToProperty(owner) {
        const ownerRecord = getOwnerRecord(owner);
        const ownerid = ownerRecord?.owner_id;

        if (!ownerid) {
            alert("Unable to add owner: owner_id is missing.");
            return;
        }

        for (var i = 0; i < properties.length; i++) {
            const confirmed = window.confirm(
                "Link this owner to property " + properties[i].property_key + "?"
            );

            if (!confirmed) {
                continue;
            }
            {
                let property_owner = {
                    property_key: properties[i].property_key,
                    owner_id: ownerid,
                    registered_owner: true,
                    share: 100,
                    owner_preamble: null,
                    owner_postamble: null,
                    sort_order: 1,
                    suppress_address: false,
                    suppress_text: false,
                    use_alternate_text: false,
                    created_by: user,
                    last_edited_by: user
                }

                const options = { body: property_owner };
                const response = await post({
                    apiName: "colin",
                    path: "/propertyowners/new",
                    options,
                }).response;
                try {
                    const apiOwners = await response.body.json();
                    return apiOwners;
                } catch (error) {
                    alert(error + "Failed to add new owner");
                }
            }
        }

    }

    async function handleAddOwnerToPropertyAndSale(owner) {
        const ownerRecord = getOwnerRecord(owner);
        const ownerid = ownerRecord?.owner_id;

        if (!ownerid) {
            alert("Unable to add owner to sale: owner_id is missing.");
            return;
        }

        let confirmed = window.confirm(
            "Link the selected Owner to this Sale?"
        );

        if (confirmed) {
            let buyer_seller = {
                sale_id: saleid,
                owner_id: ownerid,
                owner_name: ownerRecord.owner_name,
                buyer_vendor: false,
                share: 100,
                created_by: user,
                last_edited_by: user,
                new_record: true
            }

            const result = await saveRecord(buyer_seller, "buyerSellers", "buyer_seller_id");
            if (result.result) {
                buyer_seller["buyer_seller_id"] = result.id;
                buyer_seller.new_record = false;

                const updatedBuyerSellers = Array.isArray(buyerSellers) ? [...buyerSellers, buyer_seller] : [buyer_seller];
                updateTableState(updatedBuyerSellers, "buyerSellers");

                if (typeof refreshDataSet === "function") {
                    await refreshDataSet("buyerSellers");
                }
            }
        }
    }

    async function handleAddOwnerToOwner(ownerid_var) {
        if (!ownerid) {
            alert("Unable to add parent: current owner_id is missing.");
            return;
        }

        if (ownerid === ownerid_var) {
            alert('ERROR: you cannot make an owner a parent of itself.');
            return;
        }

        const parentAlreadyExists = (existingOwnerParents || []).some(record =>
            String(record?.owner_id_parent || record?.owner_id || "") === String(ownerid_var)
        );

        if (parentAlreadyExists) {
            alert("This owner is already listed as a parent company.");
            return;
        }

        let owner_tree = {
            owner_id_child: ownerid,
            owner_id_parent: ownerid_var,
            sort_within: 99,
            created_by: user,
            last_edited_by: user
        };

        try {
            const options = { body: owner_tree };
            const response = await post({
                apiName: "colin",
                path: `/owners/${ownerid}/tree/new`,
                options,
            }).response;

            const apiOwners = await response.body.json();

            if (apiOwners.result === false && apiOwners.error_message === 'Circular Dependency') {
                alert('ERROR: adding this owner as a parent would introduce a circular dependency.');
                return;
            }

            if (typeof onOwnerParentAdded === "function") {
                await onOwnerParentAdded(apiOwners);
            }

            alert("Owner added as parent");

            return apiOwners;
        } catch (error) {
            console.error("Failed to add parent owner:", error);
            alert("Failed to add parent owner. Please try again.");
        }
    }

    function selectAll(event) {
        if (event) event.preventDefault();
        const updatedData = data.map(item => ({
            ...item,
            selected: true
        }));

        if (updateTableState) {
            updateTableState(updatedData);
        }
    }

    function deselectAll(event) {
        if (event) event.preventDefault();
        const updatedData = data.map(item => ({
            ...item,
            selected: false
        }));

        if (updateTableState) {
            updateTableState(updatedData);
        }
    }

    // --- Render Functions ---
    function renderOwners() {
        const isOwnerSearchAddToSaleTable = controlType === "table1";
        const isOwnerSearchAddToPropertyTable = controlType === "table2";
        const isOwnerSearchAddParentTable = controlType === "table3";
        const isMainOwnerSearchTable = controlType === "table";
        const shouldShowAddButton = controlType === "table" || showAddButton;

        return (
            <div className="componentRendering">
                <Row className="mb-1">
                    <Col className="text-left">
                        {controlType === "table" && (
                            <Nav>
                                <Button className="me-2" variant="primary" size="sm" onClick={selectAll}>Select All</Button>
                                <Button variant="primary" size="sm" onClick={deselectAll}>Deselect All</Button>
                            </Nav>
                        )}
                    </Col>
                    <Col className="text-right">
                        {shouldShowAddButton && (
                            <Link to="/owners/new">
                                <Button variant="primary" onClick={() => { if (typeof onClose === "function") onClose(); }}>
                                    Add
                                </Button>
                            </Link>
                        )}
                    </Col>
                </Row>

                {/* Record Count */}
                {controlType === "table" && (
                    <Row className="mb-1">
                        <Col><b>{data.length} of {data[0]?.result_count || "0"} records returned.</b></Col>
                    </Row>
                )}

                <Row className="show-Container">
                    <Col sm={12} md={12} lg={12}>
                        {isLoading ? (
                            <div className="outer-div">
                                <div className="sweet-loading inner-div">
                                    <PacmanLoader
                                        cssOverride={{ display: "block", margin: "0 auto" }}
                                        size={20}
                                        color={"#e03c31"}
                                        loading={isLoading}
                                    />
                                </div>
                            </div>
                        ) : (
                            isOwnerSearchAddToSaleTable ? (
                                <GenericTable
                                    columns={columns1}
                                    data={tableData}
                                    section="owner-search-add-to-sale"
                                    pageSize={PAGE_SIZES.holdChecksSaleOwnersSection}
                                    enablePagination={true}
                                    enforceMinRowsHeight={true}
                                    minVisibleRows={5}
                                />
                            ) : isOwnerSearchAddToPropertyTable ? (
                                <GenericTable
                                    columns={columns2}
                                    data={tableData}
                                    section="owner-search-add-to-property"
                                    pageSize={PAGE_SIZES.ownerSearch}
                                    enablePagination={true}
                                    enforceMinRowsHeight={true}
                                    minVisibleRows={5}
                                />
                            ) : isOwnerSearchAddParentTable ? (
                                <GenericTable
                                    columns={columns3}
                                    data={tableData}
                                    section="owner-search-add-parent"
                                    pageSize={PAGE_SIZES.ownerSearch}
                                    enablePagination={true}
                                    enforceMinRowsHeight={true}
                                    minVisibleRows={5}
                                />
                            ) : isMainOwnerSearchTable ? (
                                <GenericTable
                                    columns={columns}
                                    data={tableData}
                                    section="owner-search"
                                    elementIds={tableElementIds}
                                    pageSize={PAGE_SIZES.ownerSearch}
                                    enablePagination={true}
                                    state={{ sorting }}
                                    onSortingChange={setSorting}
                                    enableSortingRemoval={false}
                                    meta={{
                                        handleSave: async ({ row, column, value }) => {
                                            return true;
                                        },
                                        updateData: (rowIndex, columnId, value) => {
                                            const updated = tableData.map((row, index) =>
                                                index === rowIndex ? { ...row, [columnId]: value } : row
                                            );
                                            updateTableState(updated);
                                        }
                                    }}
                                />
                            ) : null
                        )}
                    </Col>
                </Row>
            </div>
        );
    }
    return (
        <div className="OwnerPageList">
            {isAuthenticated ? renderOwners() : null}
        </div>
    );
}

export default OwnerPageList;